import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiString as API_URL } from '../utils/constants';
import { AppThunk } from './store';

import { saveEngagementDraft } from './documentEngagement.slice';
import { copyObject } from '../utils/common';
import { SummaryNoteTypeEnum } from '../Enum/SummaryNoteTypeEnum';
import { gotoNextMenu, setAssessmentError } from './assessment.slice';
import { LegalHistoryModel } from '../Models/LegalHistory/LegalHistory.model';
import { preparePriorAssessmentForNewDraft } from '../utils/aiFlagHelpers';

const legalHistorySlice = createSlice({
    name: 'legalHistorySlice',
    initialState: { legalHistoryInfo: new LegalHistoryModel(), isFetchingLegalHistory: false },
    reducers: {
        _setIsFetchingLegalHistory(state, action) {
            state.isFetchingLegalHistory = action.payload;
        },
        _setLegalHistoryInfo(state, action) {
            state.legalHistoryInfo = action.payload;
        },
    },
});

const { _setIsFetchingLegalHistory, _setLegalHistoryInfo } = legalHistorySlice.actions;

export const setLegalHistoryInfo =
    (hamiltonAnxietyScale: LegalHistoryModel): AppThunk =>
    async (dispatch) => {
        dispatch(_setLegalHistoryInfo(hamiltonAnxietyScale));
    };

export const setIsFetchingLegalHistory =
    (isFetching: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsFetchingLegalHistory(isFetching));
    };

export const saveLegalHistoryInfo =
    (legalHistoryModel: LegalHistoryModel, _saveLocal = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch) => {
        const isUpdate = Boolean(legalHistoryModel.Id);
        const url = isUpdate ? `${API_URL}/legalhistory/updatelegalhistory/` : `${API_URL}/legalhistory/insertlegalhistory/`;

        try {
            const response = await axios.put(url, legalHistoryModel);
            const savedLegalHistory = isUpdate ? legalHistoryModel : response.data;
            if (Boolean(response.data)) {
                dispatch(setLegalHistoryInfo(isUpdate ? legalHistoryModel : { ...legalHistoryModel, Id: response.data.Id }));
            }
            const isDraftSaved = await dispatch(prepareLegalHistoryNotes(savedLegalHistory));
            if (gotoNext && isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            return isDraftSaved;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        }
    };

const prepareLegalHistoryNotes =
    (legalHistoryInfo: LegalHistoryModel): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (Boolean(legalHistoryInfo.Id)) {
            let engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.LegalHistory);
            summaryNotes.push({
                Id: legalHistoryInfo.MemberId,
                CptCode: '',
                Zcode: '',
                Note: 'Legal History/Justice Involvement Completed',
                Type: SummaryNoteTypeEnum.LegalHistory,
                IsConfirm: true,
                DisplayName: 'Legal History',
            });
            if (legalHistoryInfo.YesPastNoIncarceration || legalHistoryInfo.YesOnProbataionNoIncarceration) {
                summaryNotes.push({
                    Id: legalHistoryInfo.Id,
                    CptCode: 'T1016',
                    Zcode: 'Z65.0',
                    Note: 'Conviction in civil and criminal proceedings without imprisonment',
                    Type: SummaryNoteTypeEnum.LegalHistory,
                    IsConfirm: true,
                    OrderBy: 3,
                    DisplayName: 'Legal History/Justice Involvement',
                });
            }
            if (legalHistoryInfo.YesPreviouslyIncarceratedNotOnParole || legalHistoryInfo.YesPreviouslyIncarceratedOnParole) {
                summaryNotes.push({
                    Id: legalHistoryInfo.Id,
                    CptCode: 'T1016',
                    Zcode: 'Z65.2',
                    Note: 'Problems related to release from prison',
                    Type: SummaryNoteTypeEnum.LegalHistory,
                    IsConfirm: true,
                    OrderBy: 3,
                    DisplayName: 'Legal History/Justice Involvement',
                });
            }
            if (legalHistoryInfo.YesCurrentlyChargedWarrents) {
                summaryNotes.push({
                    Id: legalHistoryInfo.Id,
                    CptCode: 'T1016',
                    Zcode: 'Z65.3',
                    Note: 'Problems related to other legal circumstances',
                    Type: SummaryNoteTypeEnum.LegalHistory,
                    IsConfirm: true,
                    OrderBy: 3,
                    DisplayName: 'Legal History/Justice Involvement',
                });
            }
            const summaryNotesWithUniqueZcodes = summaryNotes.filter(
                (note, index, self) =>
                    index === self.findIndex((n) => n.Type === note.Type && n.Zcode === note.Zcode && n.CptCode === note.CptCode && n.Note === note.Note)
            );
            const updatedEngagementDraft = {
                ...engagementDraft,
                SummaryNotes: summaryNotesWithUniqueZcodes,
                LegalHistoryId: legalHistoryInfo.Id,
            };
            const updatedMemberAssessmentStatuses = {
                ...memberAssessmentHistory.MemberAssessmentStatuses,
                LegalHistoryStatus: legalHistoryInfo.AssessmentStatus,
            };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
                EngagementDraft: updatedEngagementDraft,
            };

            return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        return false;
    };

export const fetchLegalHistoryInfo =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingLegalHistory(true));
        const response = await axios.get(`${API_URL}/legalhistory/fetchlegalhistoryinfobyid?id=${id}`);
        dispatch(setLegalHistoryInfo(response.data));
        dispatch(setIsFetchingLegalHistory(false));
    };

export const fetchLegalHistoryInfoByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingLegalHistory(true));
        const response = await axios.get(`${API_URL}/legalhistory/fetchlegalhistoryinfobymemberid?memberId=${memberId}`);
        if (response.data?.Id) {
            preparePriorAssessmentForNewDraft(response.data);
            dispatch(setLegalHistoryInfo(response.data));
        } else {
            dispatch(setLegalHistoryInfo(new LegalHistoryModel()));
        }
        dispatch(setIsFetchingLegalHistory(false));
    };

export default legalHistorySlice.reducer;
